import { DataAmigavel, MESES, diasDaSemana } from '@/shared/utils/datas';
import { GetGastoProjecaoProximaCompra, GetGastos } from './gasto.service';
import { GastoProjecaoCard } from './GastoProjecaoCard';
import { ModalGastoForm } from './ModalGastoForm';
import { ReadGastoProjecaoDto } from './gasto-projecao.types';
import { GetTipoGastos } from '../tipo-gasto/tipo-gasto.service';
import { ModalTipoGastos } from '../tipo-gasto/ModalTipoGastos';
import { resolveTipoGastoIcone } from '../tipo-gasto/tipo-gasto-icones';
import { Modal } from 'react-bootstrap';
import { ReadGastoDto, ReadTipoGastoDto } from '@/api.types';
import { toast } from 'react-toastify';
import { useBreadcrumbActions } from '@/shared/contexts/BreadcrumbActionsContext';
import { useCallback, useEffect, useState, useMemo } from 'react';
import { LuChevronLeft, LuChevronRight, LuTags } from 'react-icons/lu';

function inicioDoMes(data = new Date()) {
  return new Date(data.getFullYear(), data.getMonth(), 1);
}

export default function GastoIndex() {
  const { setActions } = useBreadcrumbActions();
  const [gastos, setGastos] = useState<ReadGastoDto[]>([]);
  const [tipoGastos, setTipoGastos] = useState<ReadTipoGastoDto[]>([]);
  const [mesReferencia, setMesReferencia] = useState(inicioDoMes);
  const [modalGastoAberto, setModalGastoAberto] = useState(false);
  const [gastosSelecionados, setGastosSelecionados] = useState<ReadGastoDto[]>([]);
  const [projecoes, setProjecoes] = useState<ReadGastoProjecaoDto[]>([]);
  const [projecoesLoading, setProjecoesLoading] = useState(false);
  const [modalTiposAberto, setModalTiposAberto] = useState(false);
  const [modalFormAberto, setModalFormAberto] = useState(false);
  const [gastoEditandoId, setGastoEditandoId] = useState(0);

  const abrirModalGasto = (gastosDoDia: ReadGastoDto[]) => {
    setGastosSelecionados(gastosDoDia);
    setModalGastoAberto(true);
  };

  const carregarGastos = useCallback(() => {
    GetGastos(0, 1000)
      .then((result) => {
        setGastos(result.objects);
      })
      .catch((res) => {
        toast.error(String(res) || '');
      });
  }, []);

  const carregarTipos = useCallback(() => {
    GetTipoGastos(0, 1000)
      .then((result) => {
        setTipoGastos(result.objects);
      })
      .catch((res) => {
        toast.error(String(res));
      });
  }, []);

  useEffect(() => {
    carregarGastos();
  }, [carregarGastos]);

  useEffect(() => {
    carregarTipos();
  }, [carregarTipos]);

  useEffect(() => {
    setActions({
      primary: {
        label: 'Tipos Gastos',
        onClick: () => setModalTiposAberto(true),
        icon: LuTags,
        variant: 'outline',
      },
    });
    return () => setActions({});
  }, [setActions]);

  useEffect(() => {
    if (tipoGastos.length === 0) {
      setProjecoes([]);
      return;
    }

    let cancelado = false;
    setProjecoesLoading(true);

    Promise.allSettled(
      tipoGastos.map((tipo) => GetGastoProjecaoProximaCompra(tipo.id))
    )
      .then((resultados) => {
        if (cancelado) return;
        setProjecoes(
          resultados
            .filter((r): r is PromiseFulfilledResult<ReadGastoProjecaoDto> => r.status === 'fulfilled')
            .map((r) => r.value)
        );
      })
      .finally(() => {
        if (!cancelado) setProjecoesLoading(false);
      });

    return () => {
      cancelado = true;
    };
  }, [tipoGastos]);

  const handleTiposChanged = () => {
    carregarTipos();
    carregarGastos();
  };

  const handleGastosChanged = () => {
    carregarGastos();
    setTipoGastos((prev) => [...prev]);
  };

  const getDiasNoMes = (mes: number, anoMes: number) => {
    return new Date(anoMes, mes + 1, 0).getDate();
  };

  const getPrimeiroDiaSemana = (mes: number, anoMes: number) => {
    return new Date(anoMes, mes, 1).getDay();
  };

  const mesesVisiveis = useMemo(() => {
    const deslocamentos = [-1, 0, 1];

    return deslocamentos.map((deslocamento) => {
      const data = new Date(
        mesReferencia.getFullYear(),
        mesReferencia.getMonth() + deslocamento,
        1
      );
      return { mes: data.getMonth(), ano: data.getFullYear() };
    });
  }, [mesReferencia]);

  const anosVisiveis = useMemo(
    () => new Set(mesesVisiveis.map((item) => item.ano)),
    [mesesVisiveis]
  );

  const gastosParaCalendario = useMemo(() => {
    return gastos.filter((gasto) => {
      if (!gasto.data) return false;
      const dataGasto = new Date(gasto.data);
      return anosVisiveis.has(dataGasto.getFullYear());
    });
  }, [gastos, anosVisiveis]);

  const getGastosNaData = (data: Date): ReadGastoDto[] => {
    return gastosParaCalendario.filter((gasto) => {
      if (!gasto.data) return false;
      const dataGasto = new Date(gasto.data);
      return (
        dataGasto.getFullYear() === data.getFullYear() &&
        dataGasto.getMonth() === data.getMonth() &&
        dataGasto.getDate() === data.getDate()
      );
    });
  };

  const hoje = useMemo(() => {
    const h = new Date();
    return new Date(h.getFullYear(), h.getMonth(), h.getDate()).getTime();
  }, []);

  const renderMes = ({ mes, ano: anoMes }: { mes: number; ano: number }) => {
    const diasNoMes = getDiasNoMes(mes, anoMes);
    const primeiroDiaSemana = getPrimeiroDiaSemana(mes, anoMes);
    const dias: (number | null)[] = [];

    for (let i = 0; i < primeiroDiaSemana; i++) {
      dias.push(null);
    }

    for (let dia = 1; dia <= diasNoMes; dia++) {
      dias.push(dia);
    }

    return (
      <div key={`${anoMes}-${mes}`} className="col calendario-mes-col mb-3">
        <div className="card h-100 calendario-card">
          <div className="card-header text-center bg-light py-1 px-2">
            <strong className="calendario-mes-titulo">{MESES[mes]} {anoMes}</strong>
          </div>
          <div className="card-body p-1">
            <div className="calendario-mes">
              <div className="calendario-dias-semana">
                {diasDaSemana.map((dia, index) => (
                  <div
                    key={index}
                    className={`calendario-dia-semana${index === 0 || index === 6 ? ' fim-de-semana' : ''}`}
                  >
                    {dia}
                  </div>
                ))}
              </div>
              <div className="calendario-dias">
                {dias.map((dia, index) => {
                  if (dia === null) {
                    return <div key={index} className="calendario-dia vazio"></div>;
                  }

                  const data = new Date(anoMes, mes, dia);
                  const dataTime = new Date(
                    data.getFullYear(),
                    data.getMonth(),
                    data.getDate()
                  ).getTime();
                  const isHoje = dataTime === hoje;
                  const isFimDeSemana = index % 7 === 0 || index % 7 === 6;
                  const gastosNaData = getGastosNaData(data);
                  const temGasto = gastosNaData.length > 0;
                  const totalGastos = gastosNaData.reduce((sum, g) => {
                    const valor = Number(g.valor) || 0;
                    return sum + valor;
                  }, 0);
                  const iconesDoDia = temGasto
                    ? Array.from(
                        new Map(
                          gastosNaData
                            .map((g) => {
                              const tipo = tipoGastos.find((t) => t.id === g.tipoGasto?.id);
                              if (!tipo?.icone) return null;
                              return [
                                tipo.id,
                                {
                                  id: tipo.id,
                                  icone: tipo.icone,
                                  cor: tipo.cor || '#6c757d',
                                  descricao: tipo.descricao,
                                },
                              ] as const;
                            })
                            .filter((item): item is readonly [number, { id: number; icone: string; cor: string; descricao: string }] => item != null)
                        ).values()
                      ).slice(0, 3)
                    : [];

                  return (
                    <div
                      key={index}
                      className={`calendario-dia ${isHoje ? 'hoje' : ''} ${temGasto ? 'com-evento' : ''}${isFimDeSemana ? ' fim-de-semana' : ''}`}
                      onClick={() => {
                        if (temGasto) {
                          abrirModalGasto(gastosNaData);
                        }
                      }}
                      style={{
                        cursor: temGasto ? 'pointer' : 'default'
                      }}
                      title={temGasto ? `${gastosNaData.length} gasto(s) - Total: ${new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(totalGastos)}` : ''}
                    >
                      <span className="calendario-dia-numero">{dia}</span>
                      {iconesDoDia.length > 0 && (
                        <span className="calendario-dia-icones">
                          {iconesDoDia.map((item) => {
                            const Icone = resolveTipoGastoIcone(item.icone);
                            if (!Icone) return null;
                            return (
                              <Icone
                                key={item.id}
                                size={11}
                                aria-hidden
                                title={item.descricao}
                                style={{ color: item.cor }}
                              />
                            );
                          })}
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const navegarMes = (deslocamento: number) => {
    setMesReferencia((atual) =>
      new Date(atual.getFullYear(), atual.getMonth() + deslocamento, 1)
    );
  };

  return (
    <>
      <div className="row">
        <div className="col-12">
          <h3>Gastos do Setor</h3>
        </div>
      </div>
      <hr />

      <div className="d-flex align-items-center justify-content-center gap-2 mb-2">
        <button
          type="button"
          className="btn btn-outline-secondary btn-sm calendario-nav-btn"
          onClick={() => navegarMes(-1)}
          aria-label="Mês anterior"
          title="Mês anterior"
        >
          <LuChevronLeft size={18} aria-hidden />
        </button>
        <div className="calendario-meses-wrapper">
          <div className="row justify-content-center g-2">
            {mesesVisiveis.map(renderMes)}
          </div>
        </div>
        <button
          type="button"
          className="btn btn-outline-secondary btn-sm calendario-nav-btn"
          onClick={() => navegarMes(1)}
          aria-label="Próximo mês"
          title="Próximo mês"
        >
          <LuChevronRight size={18} aria-hidden />
        </button>
      </div>

      <div className="mt-4">
        {projecoesLoading && (
          <p className="text-muted">Carregando projeções...</p>
        )}
        {!projecoesLoading && projecoes.length === 0 && (
          <p className="text-muted">Nenhuma projeção disponível.</p>
        )}
        {!projecoesLoading && projecoes.length > 0 && (
          <div className="row g-3">
            {projecoes.map((item) => {
              const tipo = tipoGastos.find((t) => t.id === item.tipoGastoId);
              return (
                <div key={item.tipoGastoId} className="col-12 col-md-4">
                  <GastoProjecaoCard
                    projecao={{
                      ...item,
                      tipoGastoDescricao:
                        item.tipoGastoDescricao || tipo?.descricao || `Tipo #${item.tipoGastoId}`,
                    }}
                    icone={tipo?.icone}
                    cor={tipo?.cor}
                    gastos={gastos}
                    onGastosChanged={handleGastosChanged}
                  />
                </div>
              );
            })}
          </div>
        )}
      </div>

      <ModalTipoGastos
        show={modalTiposAberto}
        onHide={() => setModalTiposAberto(false)}
        onChanged={handleTiposChanged}
      />

      <ModalGastoForm
        show={modalFormAberto}
        onHide={() => setModalFormAberto(false)}
        onSaved={() => {
          handleGastosChanged();
          setModalGastoAberto(false);
        }}
        gastoId={gastoEditandoId}
      />

      <Modal show={modalGastoAberto} onHide={() => setModalGastoAberto(false)} size='lg'>
        <Modal.Header closeButton>
          <Modal.Title>
            Gastos do Dia - {gastosSelecionados.length > 0 && gastosSelecionados[0].data ? DataAmigavel(gastosSelecionados[0].data) : ''}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {gastosSelecionados.length > 0 && (
            <table className="table table-striped table-hover mb-0">
              <thead>
                <tr>
                  <th>Quem Gastou</th>
                  <th>Tipo de Gasto</th>
                  <th>Valor</th>
                  <th>Ações</th>
                </tr>
              </thead>
              <tbody>
                {gastosSelecionados.map((gasto) => (
                  <tr key={gasto.id}>
                    <td>
                      {gasto.quemGastou ? gasto.quemGastou.nome.trim() : ''}
                    </td>
                    <td>{gasto.tipoGasto?.descricao || ''}</td>
                    <td>
                      {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(Number(gasto.valor) || 0)}
                    </td>
                    <td>
                      <button
                        type="button"
                        className="btn btn-link link-secondary p-0"
                        onClick={() => {
                          setGastoEditandoId(gasto.id);
                          setModalFormAberto(true);
                        }}
                      >
                        Editar
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <td colSpan={2}>
                    <strong>Total ({gastosSelecionados.length})</strong>
                  </td>
                  <td>
                    <strong>
                      {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(
                        gastosSelecionados.reduce((sum, g) => sum + (Number(g.valor) || 0), 0)
                      )}
                    </strong>
                  </td>
                  <td />
                </tr>
              </tfoot>
            </table>
          )}
        </Modal.Body>
        <Modal.Footer>
          <button className="btn btn-secondary" onClick={() => setModalGastoAberto(false)}>
            Fechar
          </button>
        </Modal.Footer>
      </Modal>

      <style>{`
        .calendario-meses-wrapper {
          flex: 1 1 auto;
          max-width: 980px;
        }

        .calendario-mes-col {
          flex: 0 0 33.333%;
          max-width: 300px;
        }

        .calendario-nav-btn {
          flex-shrink: 0;
          width: 36px;
          height: 36px;
          padding: 0;
          display: inline-flex;
          align-items: center;
          justify-content: center;
        }

        .calendario-mes-titulo {
          font-size: 0.85rem;
        }

        .calendario-mes {
          display: flex;
          flex-direction: column;
        }

        .calendario-dias-semana {
          display: grid;
          grid-template-columns: repeat(7, 1fr);
          gap: 2px;
          margin-bottom: 3px;
        }

        .calendario-dia-semana {
          text-align: center;
          font-weight: bold;
          font-size: 0.7rem;
          padding: 3px 1px;
          color: #666;
        }

        .calendario-dia-semana.fim-de-semana {
          color: #c97a7a;
        }

        .calendario-dias {
          display: grid;
          grid-template-columns: repeat(7, 1fr);
          gap: 2px;
        }

        .calendario-dia {
          aspect-ratio: 1;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: flex-start;
          gap: 1px;
          padding: 2px 1px 1px;
          border: 1px solid #e0e0e0;
          border-radius: 3px;
          transition: all 0.2s;
          min-height: 34px;
        }

        .calendario-dia-numero {
          font-size: 0.75rem;
          line-height: 1;
          color: #555;
        }

        .calendario-dia.fim-de-semana .calendario-dia-numero {
          color: #c97a7a;
        }

        .calendario-dia-icones {
          display: flex;
          flex-wrap: wrap;
          align-items: center;
          justify-content: center;
          gap: 1px;
          line-height: 1;
        }

        .calendario-dia.vazio {
          border: none;
          background: transparent;
        }

        .calendario-dia:hover:not(.vazio) {
          background-color: #f0f0f0;
        }

        .calendario-dia.hoje {
          background-color: #ffff76;
          border-color: #e6e650;
        }

        .calendario-dia.hoje .calendario-dia-numero {
          font-weight: 700;
          color: #333;
        }

        .calendario-dia.com-evento {
          background-color: #eeeeee !important;
          border-color: #d0d0d0;
        }

        .calendario-dia.com-evento:hover {
          background-color: #e4e4e4 !important;
        }
      `}</style>
    </>
  );
}
